import React, { useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { usePartsLookupOrgData } from './hooks/usePartsLookupOrgData';
import PartsLookupForm from './components/PartsLookup/PartsLookupForm';
import { supabase } from './config/supabaseClient';
import { useOrgTheming } from './hooks/useOrgTheming';

// Import all styles - these will be bundled with the widget
import './styles/app.scss';
import './styles/widget.scss';

type ResultState =
  | { type: 'success'; firstName: string }
  | { type: 'already_opted_in'; firstName: string }
  | { type: 'phone_not_found'; organizationName: string }
  | { type: 'error'; message: string };

function PartsLookupWidgetForm({ organizationId }: { organizationId: string }) {
  const { data: orgData, loading, error: orgError } = usePartsLookupOrgData(organizationId);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ResultState | null>(null);

  useOrgTheming(
    orgData?.organizationSettings?.primary_color,
    document.querySelector('opervia-parts-lookup') as HTMLElement | null,
  );

  const handleSubmit = async (phoneNumber: string) => {
    setSubmitting(true);
    setResult(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('parts-lookup-opt-in', {
        body: {
          organizationId,
          mobilePhone: phoneNumber
        }
      });

      if (fnError) {
        throw new Error(fnError.message);
      }

      if (!data.success) {
        if (data.error === 'phone_not_found') {
          setResult({
            type: 'phone_not_found',
            organizationName: data.organizationName || orgData?.organization?.name || 'the dealership'
          });
        } else {
          setResult({ type: 'error', message: data.error || 'An unknown error occurred.' });
        }
        return;
      }

      if (data.alreadyOptedIn) {
        setResult({ type: 'already_opted_in', firstName: data.firstName });
      } else {
        setResult({ type: 'success', firstName: data.firstName });

        // Dispatch custom event for parent page
        window.dispatchEvent(new CustomEvent('opervia-parts-lookup-success', {
          detail: { firstName: data.firstName }
        }));
      }
    } catch (err: any) {
      if (err?.context?.status === 404) {
        try {
          const errorData = JSON.parse(err.context?.body || '{}');
          if (errorData.error === 'phone_not_found') {
            setResult({
              type: 'phone_not_found',
              organizationName: errorData.organizationName || orgData?.organization?.name || 'the dealership'
            });
            return;
          }
        } catch { /* fall through */ }
      }
      setResult({ type: 'error', message: err.message || 'Submission failed. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading-indicator">Loading...</div>;
  }

  if (orgError || !orgData?.organization) {
    return <div className="error-message">{orgError || 'Organization not found.'}</div>;
  }

  const orgName = orgData.organization.name;

  if (result) {
    if (result.type === 'success') {
      return (
        <div className="parts-lookup-card">
          <div className="parts-lookup-result result-success">
            <div className="result-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
            </div>
            <h3>You're all set, {result.firstName}!</h3>
            <p>You can now text any part number to check availability.</p>
          </div>
        </div>
      );
    }
    if (result.type === 'already_opted_in') {
      return (
        <div className="parts-lookup-card">
          <div className="parts-lookup-result result-info">
            <div className="result-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
            </div>
            <h3>Already Signed Up</h3>
            <p>This phone number is already signed up for parts lookup, {result.firstName}.</p>
          </div>
        </div>
      );
    }
    if (result.type === 'phone_not_found') {
      return (
        <div className="parts-lookup-card">
          <div className="parts-lookup-result result-error">
            <div className="result-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
            </div>
            <h3>Phone Number Not Found</h3>
            <p>We couldn't find this phone number in our system. Please contact {result.organizationName} to get set up.</p>
          </div>
        </div>
      );
    }
    // error
    return (
      <div className="parts-lookup-card">
        <div className="parts-lookup-result result-error">
          <div className="result-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
          </div>
          <h3>Something Went Wrong</h3>
          <p>{result.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="public-form-page opervia-widget">
      <div className="public-form-app-container">
        <header className="form-header">
          <div className="form-header-content">
            <div className="header-text">
              <h1>{orgName}</h1>
              <h2>Parts Lookup</h2>
            </div>
          </div>
        </header>

        <div className="parts-lookup-card">
          <PartsLookupForm
            orgName={orgName}
            onSubmit={handleSubmit}
            submitting={submitting}
          />
        </div>
      </div>
    </div>
  );
}

// Custom Element Definition
class OperviaPartsLookupElement extends HTMLElement {
  private root: Root | null = null;
  private mountPoint: HTMLDivElement | null = null;

  static get observedAttributes() {
    return ['org-id'];
  }

  connectedCallback() {
    const orgId = this.getAttribute('org-id');

    if (!orgId) {
      console.error('Opervia Parts Lookup Widget: org-id attribute is required');
      this.innerHTML = '<div style="padding: 20px; color: red;">Error: org-id attribute is required</div>';
      return;
    }

    // Create mount point
    this.mountPoint = document.createElement('div');
    this.mountPoint.className = 'opervia-parts-lookup-widget-root';
    this.appendChild(this.mountPoint);

    // Mount React app
    this.root = createRoot(this.mountPoint);
    this.root.render(
      <React.StrictMode>
        <PartsLookupWidgetForm organizationId={orgId} />
      </React.StrictMode>
    );

    // Notify parent that widget is loaded
    window.dispatchEvent(new CustomEvent('opervia-parts-lookup-loaded'));
  }

  disconnectedCallback() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'org-id' && oldValue !== newValue && this.root && this.mountPoint) {
      this.root.render(
        <React.StrictMode>
          <PartsLookupWidgetForm organizationId={newValue} />
        </React.StrictMode>
      );
    }
  }
}

// Register the custom element
if (!customElements.get('opervia-parts-lookup')) {
  customElements.define('opervia-parts-lookup', OperviaPartsLookupElement);
}

// Export for module usage
export { OperviaPartsLookupElement, PartsLookupWidgetForm };

// Auto-initialize log
if (typeof window !== 'undefined') {
  console.log('Opervia Parts Lookup Widget loaded successfully');
}
